import React, { useEffect, useMemo, useState } from "react";
import styles from "../Styles/Portalconfig.module.css";

import { useUsers } from "../Commoncontext/UsersContext";
import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useCompanyConfig } from "../Commoncontext/CompanyConfigContext";
import { useToast } from "../Commoncontext/ToastContext";

/* Admin poistettu roolivalinnoista */
const ROLE_OPTIONS = [
  { key: "asukas", label: "Asukas" },
  { key: "osakas", label: "Osakas" },
  { key: "hallitus", label: "Hallitus" },
  { key: "isännöitsijä", label: "Isännöitsijä" },
];

function hasManageRights(user, selectedCompanyId) {
  const roles = user?.userRoles || [];
  const companyRoles = roles
    .filter((r) => r.companyId === selectedCompanyId)
    .map((r) => (r.role || "").toLowerCase());

  return companyRoles.includes("hallitus") || companyRoles.includes("isännöitsijä") || companyRoles.includes("admin");
}

function hasAdminRights(user, selectedCompanyId) {
  const roles = user?.userRoles || [];
  const companyRoles = roles
    .filter((r) => r.companyId === selectedCompanyId)
    .map((r) => (r.role || "").toLowerCase());

  return companyRoles.includes("admin");
}

function normalizeItems(items) {
  const arr = Array.isArray(items) ? [...items] : [];
  arr.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

  return arr.map((it, idx) => ({
    ...it,
    enabled: it.enabled !== false,
    visibleForRoles: Array.isArray(it.visibleForRoles) ? it.visibleForRoles : [],
    visibleForUsers: Array.isArray(it.visibleForUsers) ? it.visibleForUsers : [],
    order: Number.isFinite(it.order) ? it.order : (idx + 1) * 10,
    label: typeof it.label === "string" ? it.label : "",
  }));
}

function stableStringify(obj) {
  return JSON.stringify(obj);
}

function getUserId(u) {
  return u?.uid || u?.id || u?.userId || u?._id || "";
}

function getUserLabel(u) {
  const dn = (u?.displayName || "").trim();
  const fn = (u?.etunimi || "").trim();
  const ln = (u?.sukunimi || "").trim();
  const email = (u?.sähköposti || "").trim();

  if (dn) return dn;
  const full = `${fn} ${ln}`.trim();
  if (full) return full;
  if (email) return email;
  return getUserId(u) || "Käyttäjä";
}

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

/* Visibility meta: OFF / USERS / ALL / LIMITED */
function getVisibilityMeta(item) {
  if (!item?.enabled) {
    return { mode: "OFF", text: "Pois käytöstä", cls: "pillOff", icon: "⛔" };
  }

  const users = item?.visibleForUsers || [];
  if (users.length > 0) {
    // Teksti ylikirjoitetaan renderissä muodossa: "Rajatuille (N)"
    return { mode: "USERS", text: "Rajatuille", cls: "pillUsers", icon: "👤" };
  }

  const roles = item?.visibleForRoles || [];
  if (!roles.length) {
    return { mode: "ALL", text: "Näkyy kaikille", cls: "pillAll", icon: "✓" };
  }

  const allRoleKeys = ROLE_OPTIONS.map((r) => r.key);
  const allSelected = allRoleKeys.every((k) => roles.includes(k));
  if (allSelected) {
    return { mode: "ALL", text: "Näkyy kaikille", cls: "pillAll", icon: "✓" };
  }

  return { mode: "LIMITED", text: "Näkyy rajatusti", cls: "pillLimited", icon: "⚠" };
}

function Portalconfig() {
  // HUOM: jos teillä userlista tulee eri nimellä, muuta tämä rivi:
  const { user, users: allUsers } = useUsers();
  const { selectedCompanyId } = useSelectedCompany();
  const { sidebarItems, loading, updateSidebarConfig } = useCompanyConfig();
  const { showToast } = useToast();

  const canManage = useMemo(
    () => hasManageRights(user, selectedCompanyId),
    [user, selectedCompanyId]
  );

  const isAdmin = useMemo(
    () => hasAdminRights(user, selectedCompanyId),
    [user, selectedCompanyId]
  );

  const [items, setItems] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type:'ok'|'err', text:''}

  // Label edit modal
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelEditIndex, setLabelEditIndex] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");

  // USERS modal
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersEditIndex, setUsersEditIndex] = useState(null);
  const [usersDraft, setUsersDraft] = useState([]); // array of userIds
  const [usersSearch, setUsersSearch] = useState("");

  useEffect(() => {
    if (!selectedCompanyId) return;
    const normalized = normalizeItems(sidebarItems);
    setItems(normalized);
    setInitialSnapshot(stableStringify(normalized));
    setStatus(null);
  }, [sidebarItems, selectedCompanyId]);

  const dirty = useMemo(() => {
    return stableStringify(items) !== initialSnapshot;
  }, [items, initialSnapshot]);

 const selectableUsers = useMemo(() => {
  const list = Array.isArray(allUsers) ? allUsers : [];
  const q = String(usersSearch || "").toLowerCase().trim();

  const filtered = !q
    ? list
    : list.filter((u) => {
        const blob = `${getUserLabel(u)} ${(u?.email || "")}`.toLowerCase();
        return blob.includes(q);
      });

  // valitut ensin, sitten aakkosjärjestys nimellä
  const sorted = [...filtered].sort((a, b) => {
    const aId = getUserId(a);
    const bId = getUserId(b);

    const aSel = usersDraft.includes(aId);
    const bSel = usersDraft.includes(bId);
    if (aSel !== bSel) return aSel ? -1 : 1;

    return getUserLabel(a).localeCompare(getUserLabel(b), "fi");
  });

  return sorted;
}, [allUsers, usersSearch, usersDraft]);

  const visibleItems = useMemo(() => {
    if (isAdmin) return items;
    return items.filter((it) => (it?.visibleForUsers || []).length === 0);
  }, [items, isAdmin]);

  // ===== Early returns vasta nyt =====
  if (!user || !user.suostumus) {
    return <div className={styles.page}>Ei käyttöoikeutta.</div>;
  }

  if (!selectedCompanyId) {
    return <div className={styles.page}>Valitse taloyhtiö.</div>;
  }

  if (!canManage) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              <h1>Portaalitoiminnot</h1>
            </div>
          </div>
          <div className={styles.notice}>
            Vain hallitus / admin voi muokata portaalitoimintoja.
          </div>
        </div>
      </div>
    );
  }

  function patchItem(index, patch) {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });
    setStatus(null);
  }

  function toggleEnabled(index) {
    patchItem(index, { enabled: !items[index].enabled });
  }

  function toggleRole(index, roleKey) {
    const curr = items[index].visibleForRoles || [];
    const exists = curr.includes(roleKey);
    const next = exists ? curr.filter((r) => r !== roleKey) : [...curr, roleKey];
    patchItem(index, { visibleForRoles: next });
  }

  function move(index, dir) {
    const target = dir === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= items.length) return;

    setItems((prev) => {
      const copy = [...prev];
      const temp = copy[index];
      copy[index] = copy[target];
      copy[target] = temp;
      return copy.map((it, idx) => ({ ...it, order: (idx + 1) * 10 }));
    });

    setStatus(null);
  }

  async function handleSave() {
    try {
      setSaving(true);
      setStatus(null);

      const payload = items
        .map((it, idx) => ({ ...it, order: (idx + 1) * 10 }))
        .map((it) => ({
          key: it.key,
          label: it.label,
          enabled: !!it.enabled,
          visibleForRoles: it.visibleForRoles || [],
          visibleForUsers: it.visibleForUsers || [],
          order: it.order,
        }));

      await updateSidebarConfig(selectedCompanyId, payload, items);

      setInitialSnapshot(stableStringify(items));
      showToast("Portaalitoiminnot tallennettu onnistuneesti.", "success");
      setStatus(null); // halutessa voit pitää status-rivin pois
    } catch (e) {
      showToast(e?.message || "Tallennus epäonnistui.", "error");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    const normalized = normalizeItems(sidebarItems);
    setItems(normalized);
    setInitialSnapshot(stableStringify(normalized));
    setStatus(null);
  }

  function openLabelModal(index) {
    setLabelEditIndex(index);
    setLabelDraft(items[index]?.label || "");
    setLabelModalOpen(true);
  }

  function closeLabelModal() {
    setLabelModalOpen(false);
    setLabelEditIndex(null);
    setLabelDraft("");
  }

  function saveLabelModal() {
    if (labelEditIndex === null) return;
    const nextLabel = String(labelDraft || "").trim();
    if (!nextLabel) return;
    patchItem(labelEditIndex, { label: nextLabel });
    closeLabelModal();
  }

  function onLabelModalKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      closeLabelModal();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      saveLabelModal();
    }
  }

  // ===== USERS modal =====
  function openUsersModal(index) {
    setUsersEditIndex(index);
    const current = items[index]?.visibleForUsers || [];
    setUsersDraft(uniq(current));
    setUsersSearch("");
    setUsersModalOpen(true);
  }

  function closeUsersModal() {
    setUsersModalOpen(false);
    setUsersEditIndex(null);
    setUsersDraft([]);
    setUsersSearch("");
  }

  function saveUsersModal() {
    if (usersEditIndex === null) return;
    patchItem(usersEditIndex, { visibleForUsers: uniq(usersDraft) });
    closeUsersModal();
  }

  function clearUsersRestriction() {
    if (usersEditIndex === null) return;
    patchItem(usersEditIndex, { visibleForUsers: [] });
    closeUsersModal();
  }

  function onUsersModalKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      closeUsersModal();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      saveUsersModal();
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <h1>Portaalitoiminnot</h1>
          </div>

          <div className={styles.actionsRowTop}>
            <button
              className={[styles.saveBtn, dirty ? styles.saveBtnActive : ""].join(" ")}
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              {saving ? "Tallennetaan…" : "Tallenna"}
            </button>

            <button
              className={[styles.cancelBtn, dirty ? styles.cancelBtnActive : ""].join(" ")}
              onClick={handleReset}
              disabled={!dirty || saving}
            >
              Peruuta
            </button>
          </div>
        </div>

        {loading && <div className={styles.loading}>Ladataan…</div>}

        {status?.type === "ok" && <div className={styles.statusOk}>{status.text}</div>}
        {status?.type === "err" && <div className={styles.statusErr}>{status.text}</div>}

        <div className={styles.list}>
          {visibleItems.map((item, visibleIdx) => {
            const realIndex = items.findIndex((x) => x.key === item.key);
            const meta = getVisibilityMeta(item);
            const usersCount = (item.visibleForUsers || []).length;

            const hasUserRestriction = usersCount > 0;
            const rolesDisabled = !item.enabled || hasUserRestriction;

            // MUUTOS: USERS-pillissä näytetään "Rajatuille (N)" ja se on klikattava (admin)
            const usersPillText = `Rajatuille (${usersCount})`;

            return (
              <div
                key={item.key}
                className={[styles.row, !item.enabled ? styles.rowDisabled : ""].join(" ")}
              >
                <div className={styles.rowTop}>
                  <div className={styles.leftBlock}>
                    <div className={styles.orderBadge}>{visibleIdx + 1}</div>

                    <div className={styles.labelReadWrap}>
                      <span className={styles.itemTitleText}>{item.label}</span>
                      <button
                        type="button"
                        className={styles.editIconBtn}
                        onClick={() => openLabelModal(realIndex)}
                        aria-label="Muokkaa otsikkoa"
                      >
                        ✎
                      </button>
                    </div>
                  </div>

                  <div className={styles.roleBox}>
                    {/* Visibility pill: USERS-tilassa klikattava pilli */}
                    {isAdmin && meta.mode === "USERS" ? (
                      <button
                        type="button"
                        className={[styles.visibilityPill, styles[meta.cls], styles.visibilityPillBtn].join(" ")}
                        onClick={() => openUsersModal(realIndex)}
                        aria-label="Muokkaa valittuja käyttäjiä"
                        disabled={!item.enabled}
                      >
                        <span className={styles.pillIcon} aria-hidden="true">
                        <svg
                            className={styles.pillSvgIcon}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                            d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            />
                            <path
                            d="M20 22a8 8 0 0 0-16 0"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            />
                        </svg>
                        </span>
                        {usersPillText}
                      </button>
                    ) : (
                      <span className={[styles.visibilityPill, styles[meta.cls]].join(" ")}>
                        <span className={styles.pillIcon} aria-hidden="true">
                          {meta.icon}
                        </span>
                        {meta.mode === "USERS" ? usersPillText : meta.text}
                      </span>
                    )}

                    {/* MUUTOS: Config-ikoni pillien oikealle puolelle (valkoinen, avaa saman users-modal) */}
                    {isAdmin && item.enabled && (
                      <button
                        type="button"
                        className={styles.configIconBtn}
                        onClick={() => openUsersModal(realIndex)}
                        aria-label="Rajaa näkyvyys valituille käyttäjille"
                        title="Rajaa valituille käyttäjille"
                      >
                        ⚙
                      </button>
                    )}

                    {/* Roolit pysyy rivillä ja muokattavissa */}
                    {ROLE_OPTIONS.map((r) => (
                      <label key={r.key} className={styles.roleItem}>
                        <input
                          type="checkbox"
                          checked={(item.visibleForRoles || []).includes(r.key)}
                          onChange={() => toggleRole(realIndex, r.key)}
                          disabled={rolesDisabled}
                        />
                        <span className={styles.roleText}>{r.label}</span>
                      </label>
                    ))}

                    {/* POISTETTU: erillinen käyttäjämäärä-pilli + "Rajaa valituille" linkki */}
                  </div>

                  <div className={styles.controls}>
                    <button
                      type="button"
                      className={styles.arrowBtn}
                      onClick={() => move(realIndex, "up")}
                      disabled={realIndex === 0}
                      aria-label="Siirrä ylös"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.arrowBtn}
                      onClick={() => move(realIndex, "down")}
                      disabled={realIndex === items.length - 1}
                      aria-label="Siirrä alas"
                    >
                      ↓
                    </button>

                    <label className={styles.toggle}>
                      <input
                        type="checkbox"
                        checked={!!item.enabled}
                        onChange={() => toggleEnabled(realIndex)}
                      />
                      <span className={styles.toggleLabel}>
                        {item.enabled ? "Sallittu" : "Estetty"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Label edit modal */}
      {labelModalOpen && (
        <div
          className={styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeLabelModal();
          }}
        >
          <div className={styles.modalCard} onKeyDown={onLabelModalKeyDown}>
            <div className={styles.modalTitle}>Muokkaa otsikkoa</div>

            <input
              className={styles.modalInput}
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              autoFocus
            />

            <div className={styles.modalActions}>
              <button type="button" className={styles.modalCancelBtn} onClick={closeLabelModal}>
                Peruuta
              </button>
              <button
                type="button"
                className={styles.modalSaveBtn}
                onClick={saveLabelModal}
                disabled={!String(labelDraft || "").trim()}
              >
                Tallenna
              </button>
            </div>
          </div>
        </div>
      )}

      {/* USERS modal */}
      {usersModalOpen && (
        <div
          className={styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeUsersModal();
          }}
        >
          <div className={styles.modalCardWide} onKeyDown={onUsersModalKeyDown}>
            <div className={styles.modalTitle}>Valitse käyttäjät</div>

            <div className={styles.modalHint}>
              Tämä näkyvyys koskee vain valittuja käyttäjiä (beta / testaus).
            </div>

            <input
              className={styles.modalInput}
              value={usersSearch}
              onChange={(e) => setUsersSearch(e.target.value)}
              placeholder="Hae…"
              autoFocus
            />

            <div className={styles.userList}>
              {selectableUsers.length === 0 && (
                <div className={styles.userEmpty}>Ei käyttäjiä listattavaksi.</div>
              )}

             {selectableUsers.map((u) => {
                const id = getUserId(u);
                if (!id) return null;

                const checked = usersDraft.includes(id);
                const name = getUserLabel(u);
                const email = (u?.sähköposti || "").trim();

            return (
                <label key={id} className={styles.userRow}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                    setUsersDraft((prev) => {
                        const exists = prev.includes(id);
                        return exists ? prev.filter((x) => x !== id) : [...prev, id];
                    });
                    }}
                />

                <div className={styles.userMeta}>
                    <div className={styles.userName}>{name}</div>
                    {email ? <div className={styles.userSub}>{email}</div> : null}
                </div>
                </label>
            );
            })}
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.modalDangerBtn} onClick={clearUsersRestriction}>
                Tyhjennä (palaa rooleihin)
              </button>

              <div className={styles.modalActionsRight}>
                <button type="button" className={styles.modalCancelBtn} onClick={closeUsersModal}>
                  Peruuta
                </button>
                <button
                  type="button"
                  className={styles.modalSaveBtn}
                  onClick={saveUsersModal}
                  disabled={usersDraft.length === 0}
                >
                  Tallenna
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Portalconfig;
